import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Box, 
  CircularProgress, 
  Snackbar, 
  Alert, 
  Grid, 
  TextField, 
  Divider, 
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { 
  getSolutionProposal, 
  updateSolutionDetails, 
  generateSolutionProposal, 
  exportDocx, 
  exportPdf 
} from '../utils/solutionProposalService';

// Main component
const SolutionProposalPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
  
  // Solution proposal data
  const [solutionProposal, setSolutionProposal] = useState(null);
  const [solutionDetails, setSolutionDetails] = useState({
    decision_title: '',
    work_stream: '',
    decision_id: '',
    decision_owners: '',
    status: '',
    mm_functional_lead: '',
    delivery_lead: '',
    workstream_leaders: '',
    approve_date: '',
    reject_date: '',
    prepared_by: '',
    other_comments: '',
    decision_needed: '',
    option_a: {
      title: 'Option A',
      pros: [],
      cons: [],
      change_impact: '',
      resource_impact: '',
      schedule_impact: '',
      budget_impact: '',
      wricef_impact: ''
    },
    option_b: {
      title: 'Option B',
      pros: [],
      cons: [],
      change_impact: '',
      resource_impact: '',
      schedule_impact: '',
      budget_impact: '',
      wricef_impact: ''
    },
    recommendation: ''
  });

  // Fetch solution proposal data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSolutionProposal(projectId);
        
        if (!data) {
          throw new Error('No data received from the server');
        }
        
        setSolutionProposal(data.solution_proposal || null);
        
        // Check if solution details exist and initialize
        if (data.content && data.content.solution_details) {
          // Process the loaded data to ensure proper format
          const loadedDetails = data.content.solution_details;
          
          // Ensure pros and cons are arrays
          if (loadedDetails.option_a) {
            loadedDetails.option_a.pros = Array.isArray(loadedDetails.option_a.pros) ? loadedDetails.option_a.pros : [];
            loadedDetails.option_a.cons = Array.isArray(loadedDetails.option_a.cons) ? loadedDetails.option_a.cons : [];
          }
          if (loadedDetails.option_b) {
            loadedDetails.option_b.pros = Array.isArray(loadedDetails.option_b.pros) ? loadedDetails.option_b.pros : [];
            loadedDetails.option_b.cons = Array.isArray(loadedDetails.option_b.cons) ? loadedDetails.option_b.cons : [];
          }
          
          setSolutionDetails(loadedDetails);
        } else {
          // Set default dummy data if no solution details available
          setSolutionDetails({
            decision_title: 'Standardize Requisitioning Process Using SAP Fiori Apps in S/4HANA Private Edition',
            work_stream: 'Procurement (MM/P2P)',
            decision_id: 'DE001',
            decision_owners: 'Procurement Process Owner',
            status: 'Under Review',
            mm_functional_lead: '',
            delivery_lead: '',
            workstream_leaders: '',
            approve_date: '',
            reject_date: '',
            prepared_by: 'Functional Consultant, MM',
            other_comments: 'This decision will influence requisitioner experience, training scope, and approval workflow readiness across business units.',
            decision_needed: 'Decide whether to adopt standard SAP Fiori requisitioning apps (such as "Manage Purchase Requisitions" and "Create Purchase Requisition") or build custom Fiori apps to replicate legacy functionality and user experience.',
            option_a: {
              title: 'Adopt Standard SAP Fiori Requisitioning Apps',
              pros: [
                'SAP Best Practice aligned',
                'No custom code, easier upgrades',
                'Faster deployment with lower support needs'
              ],
              cons: [
                'May require process changes',
                'Some legacy UI features may not exist'
              ],
              change_impact: 'People: Training for new interface\nProcess: Some process realignment\nTechnology: Leverages standard SAP',
              resource_impact: 'Low, primarily functional and training effort',
              schedule_impact: '+2 weeks for testing and enablement',
              budget_impact: 'Minimal',
              wricef_impact: '1 configuration transport\nNo custom developments'
            },
            option_b: {
              title: 'Develop Custom Requisitioning Apps in Fiori',
              pros: [
                'Replicates familiar legacy design',
                'Customized approval workflows'
              ],
              cons: [
                'Higher build and support effort',
                'Greater testing burden during upgrades'
              ],
              change_impact: 'People: No major change for users\nProcess: Minimal change\nTechnology: Custom UI5 and workflow objects',
              resource_impact: 'High, Fiori, workflow, and QA teams required',
              schedule_impact: '+6–8 weeks',
              budget_impact: 'Moderate to High',
              wricef_impact: '2 custom Fiori apps\n1 workflow enhancement\n1 configuration transport'
            },
            recommendation: 'Option A is recommended to reduce complexity, align with SAP standard, and accelerate time to value. Custom enhancements can be evaluated post-go-live if strong business needs arise.'
          });
        }
        
      } catch (error) {
        console.error('Error fetching solution proposal data:', error);
        
        // Set default dummy data on error
        setSolutionDetails({
          decision_title: 'Standardize Requisitioning Process Using SAP Fiori Apps in S/4HANA Private Edition',
          work_stream: 'Procurement (MM/P2P)',
          decision_id: 'DE001',
          decision_owners: 'Procurement Process Owner',
          status: 'Under Review',
          mm_functional_lead: '',
          delivery_lead: '',
          workstream_leaders: '',
          approve_date: '',
          reject_date: '',
          prepared_by: 'Functional Consultant, MM',
          other_comments: 'This decision will influence requisitioner experience, training scope, and approval workflow readiness across business units.',
          decision_needed: 'Decide whether to adopt standard SAP Fiori requisitioning apps (such as "Manage Purchase Requisitions" and "Create Purchase Requisition") or build custom Fiori apps to replicate legacy functionality and user experience.',
          option_a: {
            title: 'Adopt Standard SAP Fiori Requisitioning Apps',
            pros: [
              'SAP Best Practice aligned',
              'No custom code, easier upgrades',
              'Faster deployment with lower support needs'
            ],
            cons: [
              'May require process changes',
              'Some legacy UI features may not exist'
            ],
            change_impact: 'People: Training for new interface\nProcess: Some process realignment\nTechnology: Leverages standard SAP',
            resource_impact: 'Low, primarily functional and training effort',
            schedule_impact: '+2 weeks for testing and enablement',
            budget_impact: 'Minimal',
            wricef_impact: '1 configuration transport\nNo custom developments'
          },
          option_b: {
            title: 'Develop Custom Requisitioning Apps in Fiori',
            pros: [
              'Replicates familiar legacy design',
              'Customized approval workflows'
            ],
            cons: [
              'Higher build and support effort',
              'Greater testing burden during upgrades'
            ],
            change_impact: 'People: No major change for users\nProcess: Minimal change\nTechnology: Custom UI5 and workflow objects',
            resource_impact: 'High, Fiori, workflow, and QA teams required',
            schedule_impact: '+6–8 weeks',
            budget_impact: 'Moderate to High',
            wricef_impact: '2 custom Fiori apps\n1 workflow enhancement\n1 configuration transport'
          },
          recommendation: 'Option A is recommended to reduce complexity, align with SAP standard, and accelerate time to value. Custom enhancements can be evaluated post-go-live if strong business needs arise.'
        });
        
        setAlert({
          open: true,
          message: 'Using sample data as a template. Changes will be saved when you click "Save Solution Proposal".',
          severity: 'info'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [projectId]);

  // Save solution details
  const saveSolutionDetails = async () => {
    try {
      setSaving(true);
      await updateSolutionDetails(projectId, solutionDetails);
      setAlert({
        open: true,
        message: 'Solution details saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving solution details:', error);
      setAlert({
        open: true,
        message: 'Failed to save solution details',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Generate solution proposal with OpenAI
  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const response = await generateSolutionProposal(projectId);
      
      // Update the state with generated content
      if (response.updates && response.updates.solution_details) {
        const generatedDetails = response.updates.solution_details;
        
        // Ensure pros and cons are arrays
        if (generatedDetails.option_a) {
          generatedDetails.option_a.pros = Array.isArray(generatedDetails.option_a.pros) ? generatedDetails.option_a.pros : [];
          generatedDetails.option_a.cons = Array.isArray(generatedDetails.option_a.cons) ? generatedDetails.option_a.cons : [];
        }
        if (generatedDetails.option_b) {
          generatedDetails.option_b.pros = Array.isArray(generatedDetails.option_b.pros) ? generatedDetails.option_b.pros : [];
          generatedDetails.option_b.cons = Array.isArray(generatedDetails.option_b.cons) ? generatedDetails.option_b.cons : [];
        }
        
        setSolutionDetails(generatedDetails);
        
        setAlert({
          open: true,
          message: 'Solution proposal generated successfully from project documents',
          severity: 'success'
        });
      } else if (response.raw_ai_response || response.raw_response) {
        // We have AI content but couldn't process it properly
        console.log('Raw AI response:', response.raw_ai_response || response.raw_response);
        
        setAlert({
          open: true,
          message: 'Generated content received but could not be properly formatted. Please try again or contact support.',
          severity: 'warning'
        });
      } else {
        setAlert({
          open: true,
          message: response.message || 'Content generated but no solution details were updated',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error generating content:', error);
      
      // Use the userMessage extracted in the service if available
      const errorMessage = error.userMessage || 'Failed to generate content. Please make sure project documents are associated with this project.';
        
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  // Export to DOCX
  const handleExportDocx = async () => {
    try {
      setExporting(true);
      
      // First save current data to ensure we're exporting the latest changes
      try {
        await updateSolutionDetails(projectId, solutionDetails);
      } catch (saveError) {
        console.warn('Could not save data before export, proceeding with export:', saveError);
        // Continue with export even if save fails - backend will use what's in DB
      }
      
      // Call exportDocx to generate the document with current data from database
      const response = await exportDocx(projectId);
      
      if (response.document_path) {
        // Import the downloadDocumentById function from documentService
        const { downloadDocumentById } = await import('../utils/documentService');
        
        // Use the enhanced downloadDocumentById with isSolutionProposal flag
        const downloadResult = await downloadDocumentById(null, 'docx', true, projectId);
        
        if (!downloadResult) {
          throw new Error('Failed to download the document. Please try again.');
        }
      } else {
        throw new Error('Failed to generate the document. Please try again.');
      }
      
      setAlert({
        open: true,
        message: 'Document exported successfully with current data',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting document:', error);
      setAlert({
        open: true,
        message: error.message || 'Failed to export document. Please try again later.',
        severity: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Export to PDF
  const handleExportPdf = async () => {
    try {
      setExporting(true);
      
      // First save current data to ensure we're exporting the latest changes
      try {
        await updateSolutionDetails(projectId, solutionDetails);
      } catch (saveError) {
        console.warn('Could not save data before export, proceeding with export:', saveError);
        // Continue with export even if save fails - backend will use what's in DB
      }
      
      const response = await exportPdf(projectId);
      
      if (response.document_path) {
        // Import the downloadDocumentById function from documentService
        const { downloadDocumentById } = await import('../utils/documentService');
        
        // Determine the format based on the response
        const format = response.is_pdf === false ? 'docx' : 'pdf';
        
        // Use the enhanced downloadDocumentById with isSolutionProposal flag
        const downloadResult = await downloadDocumentById(null, format, true, projectId);
        
        if (!downloadResult) {
          throw new Error('Failed to download the document. Please try again.');
        }
        
        // Show appropriate message
        if (response.is_pdf === false) {
          setAlert({
            open: true,
            message: 'PDF conversion not available. Downloaded as DOCX instead.',
            severity: 'info'
          });
        } else {
          setAlert({
            open: true,
            message: 'PDF exported successfully',
            severity: 'success'
          });
        }
      } else if (response.message) {
        // Handle case where PDF generation failed completely
        setAlert({
          open: true,
          message: response.message,
          severity: 'warning'
        });
      } else {
        setAlert({
          open: true,
          message: 'PDF export failed. Please try the DOCX export instead.',
          severity: 'warning'
        });
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      
      // Check if the error response contains a specific message
      let errorMessage = 'Failed to export PDF. Please try again later.';
      if (error.response && error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setExporting(false);
    }
  };

  // Helper functions for Option A
  const updateSolutionDetailsField = (field, value) => {
    setSolutionDetails({
      ...solutionDetails,
      [field]: value
    });
  };

  // Option A helper functions
  const updateOptionAField = (field, value) => {
    setSolutionDetails({
      ...solutionDetails,
      option_a: {
        ...solutionDetails.option_a,
        [field]: value
      }
    });
  };

  const addOptionAPro = () => {
    const updatedOptionA = {
      ...solutionDetails.option_a,
      pros: [...(solutionDetails.option_a.pros || []), '']
    };
    
    setSolutionDetails({
      ...solutionDetails,
      option_a: updatedOptionA
    });
  };

  const updateOptionAPro = (index, value) => {
    const updatedPros = [...(solutionDetails.option_a.pros || [])];
    updatedPros[index] = value;
    
    setSolutionDetails({
      ...solutionDetails,
      option_a: {
        ...solutionDetails.option_a,
        pros: updatedPros
      }
    });
  };

  const removeOptionAPro = (index) => {
    const updatedPros = [...(solutionDetails.option_a.pros || [])];
    updatedPros.splice(index, 1);
    
    setSolutionDetails({
      ...solutionDetails,
      option_a: {
        ...solutionDetails.option_a,
        pros: updatedPros
      }
    });
  };

  const addOptionACon = () => {
    const updatedOptionA = {
      ...solutionDetails.option_a,
      cons: [...(solutionDetails.option_a.cons || []), '']
    };
    
    setSolutionDetails({
      ...solutionDetails,
      option_a: updatedOptionA
    });
  };

  const updateOptionACon = (index, value) => {
    const updatedCons = [...(solutionDetails.option_a.cons || [])];
    updatedCons[index] = value;
    
    setSolutionDetails({
      ...solutionDetails,
      option_a: {
        ...solutionDetails.option_a,
        cons: updatedCons
      }
    });
  };

  const removeOptionACon = (index) => {
    const updatedCons = [...(solutionDetails.option_a.cons || [])];
    updatedCons.splice(index, 1);
    
    setSolutionDetails({
      ...solutionDetails,
      option_a: {
        ...solutionDetails.option_a,
        cons: updatedCons
      }
    });
  };

  // Option B helper functions
  const updateOptionBField = (field, value) => {
    setSolutionDetails({
      ...solutionDetails,
      option_b: {
        ...solutionDetails.option_b,
        [field]: value
      }
    });
  };

  const addOptionBPro = () => {
    const updatedOptionB = {
      ...solutionDetails.option_b,
      pros: [...(solutionDetails.option_b.pros || []), '']
    };
    
    setSolutionDetails({
      ...solutionDetails,
      option_b: updatedOptionB
    });
  };

  const updateOptionBPro = (index, value) => {
    const updatedPros = [...(solutionDetails.option_b.pros || [])];
    updatedPros[index] = value;
    
    setSolutionDetails({
      ...solutionDetails,
      option_b: {
        ...solutionDetails.option_b,
        pros: updatedPros
      }
    });
  };

  const removeOptionBPro = (index) => {
    const updatedPros = [...(solutionDetails.option_b.pros || [])];
    updatedPros.splice(index, 1);
    
    setSolutionDetails({
      ...solutionDetails,
      option_b: {
        ...solutionDetails.option_b,
        pros: updatedPros
      }
    });
  };

  const addOptionBCon = () => {
    const updatedOptionB = {
      ...solutionDetails.option_b,
      cons: [...(solutionDetails.option_b.cons || []), '']
    };
    
    setSolutionDetails({
      ...solutionDetails,
      option_b: updatedOptionB
    });
  };

  const updateOptionBCon = (index, value) => {
    const updatedCons = [...(solutionDetails.option_b.cons || [])];
    updatedCons[index] = value;
    
    setSolutionDetails({
      ...solutionDetails,
      option_b: {
        ...solutionDetails.option_b,
        cons: updatedCons
      }
    });
  };

  const removeOptionBCon = (index) => {
    const updatedCons = [...(solutionDetails.option_b.cons || [])];
    updatedCons.splice(index, 1);
    
    setSolutionDetails({
      ...solutionDetails,
      option_b: {
        ...solutionDetails.option_b,
        cons: updatedCons
      }
    });
  };

  // Handle alert close
  const handleAlertClose = () => {
    setAlert({
      ...alert,
      open: false
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/projects/${projectId}`)}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Solution Proposal
          </Typography>
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<DescriptionIcon />} 
            onClick={handleExportDocx} 
            disabled={exporting}
            sx={{ mr: 1 }}
          >
            Export DOCX
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<PictureAsPdfIcon />} 
            onClick={handleExportPdf} 
            disabled={exporting}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Paper elevation={3} sx={{ mb: 4, p: 2 }}>
        {/* AI Generation Panel */}
        <Box sx={{ p: 2, mb: 4, bgcolor: '#1e2a38', borderRadius: 1, color: 'white' }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            <AutorenewIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
            Generate content with AI
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlaylistAddCheckIcon />}
                onClick={handleGenerate}
                disabled={generating}
              >
                Generate From Project Documents
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Solution Proposal Form */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Solution Proposal Information
          </Typography>
          <Grid container spacing={3}>
            {/* Header Information */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Decision Title"
                value={solutionDetails.decision_title || ''}
                onChange={(e) => updateSolutionDetailsField('decision_title', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Work Stream"
                value={solutionDetails.work_stream || ''}
                onChange={(e) => updateSolutionDetailsField('work_stream', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Decision ID"
                value={solutionDetails.decision_id || ''}
                onChange={(e) => updateSolutionDetailsField('decision_id', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Decision Owner(s)"
                value={solutionDetails.decision_owners || ''}
                onChange={(e) => updateSolutionDetailsField('decision_owners', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Status"
                value={solutionDetails.status || ''}
                onChange={(e) => updateSolutionDetailsField('status', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="MM Functional Lead"
                value={solutionDetails.mm_functional_lead || ''}
                onChange={(e) => updateSolutionDetailsField('mm_functional_lead', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Delivery Lead"
                value={solutionDetails.delivery_lead || ''}
                onChange={(e) => updateSolutionDetailsField('delivery_lead', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Work Stream Leader(s)"
                value={solutionDetails.workstream_leaders || ''}
                onChange={(e) => updateSolutionDetailsField('workstream_leaders', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Approve Date"
                type="date"
                value={solutionDetails.approve_date || ''}
                onChange={(e) => updateSolutionDetailsField('approve_date', e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Reject Date"
                type="date"
                value={solutionDetails.reject_date || ''}
                onChange={(e) => updateSolutionDetailsField('reject_date', e.target.value)}
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Prepared By"
                value={solutionDetails.prepared_by || ''}
                onChange={(e) => updateSolutionDetailsField('prepared_by', e.target.value)}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Other Comments"
                value={solutionDetails.other_comments || ''}
                onChange={(e) => updateSolutionDetailsField('other_comments', e.target.value)}
                margin="normal"
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Decision Needed */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Description of the Decision Needed
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={solutionDetails.decision_needed || ''}
            onChange={(e) => updateSolutionDetailsField('decision_needed', e.target.value)}
            margin="normal"
          />
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* Option A */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Option A
          </Typography>
          <TextField
            fullWidth
            label="Option Title"
            value={solutionDetails.option_a?.title || 'Option A'}
            onChange={(e) => updateOptionAField('title', e.target.value)}
            margin="normal"
          />
          
          {/* Pros */}
          <Typography variant="h6" sx={{ mt: 2 }}>Pros</Typography>
          <List>
            {(solutionDetails.option_a?.pros || []).map((pro, index) => (
              <ListItem key={index} disableGutters>
                <TextField
                  fullWidth
                  value={pro}
                  onChange={(e) => updateOptionAPro(index, e.target.value)}
                  margin="dense"
                />
                <IconButton onClick={() => removeOptionAPro(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
            <ListItem disableGutters>
              <Button
                startIcon={<AddIcon />}
                onClick={addOptionAPro}
              >
                Add Pro
              </Button>
            </ListItem>
          </List>
          
          {/* Cons */}
          <Typography variant="h6" sx={{ mt: 2 }}>Cons</Typography>
          <List>
            {(solutionDetails.option_a?.cons || []).map((con, index) => (
              <ListItem key={index} disableGutters>
                <TextField
                  fullWidth
                  value={con}
                  onChange={(e) => updateOptionACon(index, e.target.value)}
                  margin="dense"
                />
                <IconButton onClick={() => removeOptionACon(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
            <ListItem disableGutters>
              <Button
                startIcon={<AddIcon />}
                onClick={addOptionACon}
              >
                Add Con
              </Button>
            </ListItem>
          </List>
          
          {/* Impacts */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Change Impact (People, Process, Technology)"
                multiline
                rows={2}
                value={solutionDetails.option_a?.change_impact || ''}
                onChange={(e) => updateOptionAField('change_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Resource Impact"
                value={solutionDetails.option_a?.resource_impact || ''}
                onChange={(e) => updateOptionAField('resource_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Schedule Impact"
                value={solutionDetails.option_a?.schedule_impact || ''}
                onChange={(e) => updateOptionAField('schedule_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Budget Impact"
                value={solutionDetails.option_a?.budget_impact || ''}
                onChange={(e) => updateOptionAField('budget_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="WRICEF Impact"
                value={solutionDetails.option_a?.wricef_impact || ''}
                onChange={(e) => updateOptionAField('wricef_impact', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Option B */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Option B
          </Typography>
          <TextField
            fullWidth
            label="Option Title"
            value={solutionDetails.option_b?.title || 'Option B'}
            onChange={(e) => updateOptionBField('title', e.target.value)}
            margin="normal"
          />
          
          {/* Pros */}
          <Typography variant="h6" sx={{ mt: 2 }}>Pros</Typography>
          <List>
            {(solutionDetails.option_b?.pros || []).map((pro, index) => (
              <ListItem key={index} disableGutters>
                <TextField
                  fullWidth
                  value={pro}
                  onChange={(e) => updateOptionBPro(index, e.target.value)}
                  margin="dense"
                />
                <IconButton onClick={() => removeOptionBPro(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
            <ListItem disableGutters>
              <Button
                startIcon={<AddIcon />}
                onClick={addOptionBPro}
              >
                Add Pro
              </Button>
            </ListItem>
          </List>
          
          {/* Cons */}
          <Typography variant="h6" sx={{ mt: 2 }}>Cons</Typography>
          <List>
            {(solutionDetails.option_b?.cons || []).map((con, index) => (
              <ListItem key={index} disableGutters>
                <TextField
                  fullWidth
                  value={con}
                  onChange={(e) => updateOptionBCon(index, e.target.value)}
                  margin="dense"
                />
                <IconButton onClick={() => removeOptionBCon(index)}>
                  <DeleteIcon />
                </IconButton>
              </ListItem>
            ))}
            <ListItem disableGutters>
              <Button
                startIcon={<AddIcon />}
                onClick={addOptionBCon}
              >
                Add Con
              </Button>
            </ListItem>
          </List>
          
          {/* Impacts */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Change Impact (People, Process, Technology)"
                multiline
                rows={2}
                value={solutionDetails.option_b?.change_impact || ''}
                onChange={(e) => updateOptionBField('change_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Resource Impact"
                value={solutionDetails.option_b?.resource_impact || ''}
                onChange={(e) => updateOptionBField('resource_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Schedule Impact"
                value={solutionDetails.option_b?.schedule_impact || ''}
                onChange={(e) => updateOptionBField('schedule_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Budget Impact"
                value={solutionDetails.option_b?.budget_impact || ''}
                onChange={(e) => updateOptionBField('budget_impact', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="WRICEF Impact"
                value={solutionDetails.option_b?.wricef_impact || ''}
                onChange={(e) => updateOptionBField('wricef_impact', e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Final Recommendation */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Final Recommendation
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={solutionDetails.recommendation || ''}
            onChange={(e) => updateSolutionDetailsField('recommendation', e.target.value)}
            margin="normal"
          />
        </Box>
        
        {/* Save Button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={saveSolutionDetails}
            disabled={saving}
            size="large"
          >
            Save Solution Proposal
          </Button>
        </Box>
      </Paper>

      {/* Notifications */}
      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SolutionProposalPage;